<template>
  <div>
    <Head title="Purchase Requests" />
    <h1 class="mb-8 text-3xl font-bold">
      Transaksi
      <span class="text-indigo-400 font-medium"> / </span>
      Purchase Requests
    </h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter
        v-model="form.search"
        class="mr-4 w-full max-w-md"
        @reset="reset">
        <label class="block text-gray-700">Project:</label>
        <select v-model="form.project" class="form-select mt-1 w-full">
          <option :value="null">
            [ALL]
          </option>
          <option
            v-for="project in projects"
            :key="project.id"
            :value="project.id">
            {{ project.title }}
          </option>
        </select>
      </search-filter>
      <div class="flex mx-6 w-full bg-white rounded shadow">
        <vSelect
          v-model="form.item"
          :reduce="(item) => item.id"
          class="min-w-full focus:text-indigo-500"
          placeholder="[Filter by barang detail...]"
          label="name"
          :filterable="false"
          :options="options"
          :append-to-body="true"
          @search="onSearchItem" />
      </div>
      <Link class="btn-indigo" href="/purchases/create">
        <span>Create</span>
        <span class="hidden md:inline">&nbsp;Purchase Request</span>
      </Link>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold">
            <th class="pb-4 pt-6 px-6">
              Nomor
            </th>
            <th class="pb-4 pt-6 px-6">
              Lokasi
            </th>
            <th class="pb-4 pt-6 px-6">
              Lampiran
            </th>
            <th class="pb-4 pt-6 px-6">
              Operator
            </th>
            <th class="pb-4 pt-6 px-6">
              PR_Created
            </th>
            <th class="pb-4 pt-6 px-6">
              Status
            </th>
            <th class="pb-4 pt-6 px-6">
              Project
            </th>
            <th class="pb-4 pt-6 px-6">
              Aktifitas Project
            </th>
            <th class="pb-4 pl-6 pr-14 pt-6" colspan="2">
              Grandtotal
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="purchasereq in purchasereqs.data"
            :key="purchasereq.id"
            class="focus-within:bg-gray-100 hover:bg-gray-100">
            <td class="border-t">
              <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="`/purchases/${purchasereq.id}/edit`">
                {{ purchasereq.code }}
                <icon
                  v-if="purchasereq.deleted_at"
                  name="trash"
                  class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/purchases/${purchasereq.id}/edit`"
                tabindex="-1">
                {{ purchasereq.pr_location }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex px-10 py-4"
                :href="`/purchases/${purchasereq.id}/edit#attachments`"
                tabindex="-1">
                <icon v-if="purchasereq.has_attachment" name="attachment" />
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/purchases/${purchasereq.id}/edit`"
                tabindex="-1">
                {{ purchasereq.opr_request }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/purchases/${purchasereq.id}/edit`"
                tabindex="-1">
                {{ purchasereq.created_at }}
              </Link>
            </td>
            <td class="border-t">
              <Link
                class="flex items-center px-6 py-4"
                :href="`/purchases/${purchasereq.id}/edit`"
                tabindex="-1">
                {{ purchasereq.status }}
              </Link>
            </td>
            <td class="border-t">
              <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="`/purchases/${purchasereq.id}/edit`">
                {{ purchasereq.project }}
                <icon
                  v-if="purchasereq.deleted_at"
                  name="trash"
                  class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </Link>
            </td>
            <td class="whitespace-normal border-t">
              <Link class="flex items-center px-6 py-4 focus:text-indigo-500" :href="`/purchases/${purchasereq.id}/edit`">
                {{ purchasereq.project_act }}
                <icon
                  v-if="purchasereq.deleted_at"
                  name="trash"
                  class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </Link>
            </td>
            <td class="text-right border-t">
              {{ formatNumber(purchasereq.total_est) }}
            </td>
            <td class="w-px border-t">
              <Link
                class="flex items-center px-4"
                :href="`/purchases/${purchasereq.id}/edit`"
                tabindex="-1">
                <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
              </Link>
            </td>
          </tr>
          <tr v-if="purchasereqs.data.length === 0">
            <td class="px-6 py-4 border-t" colspan="6">
              No purchase request found.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <pagination class="mt-6" :links="purchasereqs.links" />
  </div>
</template>

<script lang="js">
import { Head, Link } from '@inertiajs/vue3'
import { ref } from 'vue'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import SearchFilter from '@/Shared/SearchFilter'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

export default {
    components: {
        Head,
        Icon,
        Link,
        Pagination,
        SearchFilter,
        vSelect,
    },
    layout: Layout,
    props: {
        projects: Object,
        filters: Object,
        purchasereqs: Object,
        defaultOptions: Object,
    },
    setup (props) {
        const options = ref(props.defaultOptions)
        const searchAutoComplete = throttle((loading, search) => {
            fetch(`/items?options=1&search=${encodeURI(search)}`).then((res) => {
                res.json().then((json) => (options.value = json.items))
                loading(false)
            })
        }, 500)
        const onSearchItem = (search, loading) => {
            if (search.length) {
                loading(true)
                searchAutoComplete(loading, search)
            }
        }
        return {
            options,
            onSearchItem,
        }
    },
    data () {
        return {
            form: {
                search: this.filters.search,
                project: this.filters.project,
                item: this.filters.item ? parseInt(this.filters.item) : null,
            },
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get('/purchases', pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        formatNumber (num) {
            return num ? num.toLocaleString('id-ID') : '0'
        },
        reset () {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
